import { useEffect, useState } from "react";
import { formatTimeRemainingUntil } from "../utils/studentRecentContent";
import {
  formatExamClosesIn,
  getStudentExamDeadlineState,
  isExamPastEndTime,
  resolveStudentExamDeadline,
  secondsUntilApiDateTime,
} from "../utils/examSchedule";

/** Tick clock for live “starts in …” / ended / closes-in labels. */
export function useCountdownNow(enabled = true, intervalMs = 30_000) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs]);

  return now;
}

export function useStartsInLabel(startTime, intervalMs = 30_000) {
  const enabled = Boolean(startTime);
  const now = useCountdownNow(enabled, intervalMs);
  return formatTimeRemainingUntil(startTime, now);
}

/** Seconds until an API datetime (Cairo-naive). Negative if already passed. */
export function useSecondsUntilApiDateTime(value, intervalMs = 1_000) {
  const enabled = Boolean(value);
  const now = useCountdownNow(enabled, intervalMs);
  return secondsUntilApiDateTime(value, now);
}

/** True when exam end_time has passed (Cairo). Re-checks on an interval. */
export function useExamPastEndTime(endTime, intervalMs = 30_000) {
  const enabled = Boolean(endTime);
  const now = useCountdownNow(enabled, intervalMs);
  return isExamPastEndTime(endTime, now);
}

/**
 * Live "closes in …" label (Cairo). Null when missing or already ended.
 */
export function useExamClosesInLabel(endTime, intervalMs = 1_000) {
  const enabled = Boolean(endTime);
  const now = useCountdownNow(enabled, intervalMs);
  return formatExamClosesIn(endTime, now);
}

/**
 * Combined end-time schedule: past flag + closes-in label (one timer).
 */
export function useExamEndSchedule(endTime, intervalMs = 1_000) {
  const enabled = Boolean(endTime);
  const now = useCountdownNow(enabled, intervalMs);
  return {
    pastEnd: isExamPastEndTime(endTime, now),
    closesIn: formatExamClosesIn(endTime, now),
  };
}

/**
 * Group due date, else exam end_time, with a 24h late window.
 * No deadline → closed false and closesIn null.
 */
export function useStudentExamDeadline(exam, studentGroupId, intervalMs = 1_000) {
  const deadline = resolveStudentExamDeadline(exam, studentGroupId);
  const now = useCountdownNow(Boolean(deadline), intervalMs);
  return getStudentExamDeadlineState(exam, studentGroupId, now);
}
